import { useState, useRef, useEffect } from 'react';
import { api } from '../App';
import { Input } from '@/components/ui/input';

/**
 * Autocomplete input for selecting a "fizičko lice" (physical person) from the database.
 *
 * Props:
 * - value: current name string
 * - onChange: (name) => void — called when user types
 * - onSelect: (fl) => void — called when user picks an entry. Parent decides which
 *   fields to populate (e.g. jmb, broj_dokumenta, vrsta_dokumenta, pol, adresa...).
 * - clientId: if provided, filters list to only workers of this client. On focus,
 *   immediately shows all workers for that client (no typing needed).
 * - placeholder, className, testid
 */
export default function FizickoLiceAutocomplete({
  value = '',
  onChange,
  onSelect,
  clientId = '',
  placeholder,
  className = 'bg-white border-slate-300 text-sm',
  testid,
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const fetchList = async (search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (clientId) params.set('client_id', clientId);
      if (search) params.set('search', search);
      const r = await api.get(`/fizicka-lica?${params.toString()}`);
      const list = (r.data || []).slice(0, 50);
      setResults(list);
      setOpen(true);
    } catch {
      setResults([]); setOpen(false);
    } finally { setLoading(false); }
  };

  const handleType = (val) => {
    onChange?.(val);
    clearTimeout(timer.current);
    // Kada je clientId zadat: uvijek filtriraj po njemu (prazna pretraga prikazuje sve radnike firme)
    // Kada clientId NIJE zadat: pretrazujemo od 2+ karaktera po ime / prezime / jmb / telefon / email
    const v = (val || '').trim();
    if (!clientId && v.length < 2) {
      setResults([]); setOpen(false); return;
    }
    timer.current = setTimeout(() => fetchList(v), 300);
  };

  const handleFocus = () => {
    // When filtering by client, always show all workers on focus (ignore current field value)
    if (clientId) fetchList('');
    else if (results.length > 0) setOpen(true);
  };

  const pick = (fl) => {
    setResults([]); setOpen(false);
    onSelect?.(fl);
  };

  const effectivePlaceholder = placeholder || (clientId ? 'Klikni za listu radnika firme' : 'Pretraga po imenu, prezimenu ili JMB-u...');

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        value={value}
        onChange={e => handleType(e.target.value)}
        onFocus={handleFocus}
        placeholder={effectivePlaceholder}
        className={className}
        data-testid={testid}
        autoComplete="off"
      />
      {open && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-xs text-slate-400">Učitavanje...</div>}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">{clientId ? 'Nema radnika u ovoj firmi' : 'Nema rezultata'}</div>
          )}
          {results.map(fl => (
            <button
              key={fl.id}
              type="button"
              onMouseDown={e => { e.preventDefault(); pick(fl); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{fl.prezime} {fl.ime}</span>
                {fl.jmb && <span className="text-xs text-slate-500">JMB: {fl.jmb}</span>}
                {fl.broj_dokumenta && <span className="text-xs text-slate-400">Dok: {fl.broj_dokumenta}</span>}
              </div>
              {!clientId && fl.client_naziv && (
                <div className="text-[11px] text-slate-400 mt-0.5">{fl.client_naziv}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
